import {
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRefresh,
  useStore,
  useUpdate,
} from "ra-core";
import { useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  CalendarClock,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  Mail,
  PenLine,
  Send,
  Underline,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import type { EmailPayload } from "@slate/shared";
import type { Contact, Sale } from "../types";
import { callNylasApi } from "../providers/supabase/nylasApi";
import { bookingLinkFor } from "../meetings/bookingApi";
import { loadBookingPage } from "../meetings/bookingPageSettings";

/**
 * Write an email from a contact - full formatting (bold/italic/underline,
 * bullets, left/center/right alignment, links, inline images) plus one-click
 * insert of your meeting link (your meeting scheduler page, set up on your profile).
 *
 * Sending: if you've connected your Google/Microsoft account (Settings → My
 * profile), this delivers for real through YOUR mailbox via Nylas - real
 * deliverability, real Sent folder - then logs to the timeline. Not
 * connected yet: falls back to logging only (the original demo behavior),
 * with a nudge to connect.
 *
 * Logging: an email.sent event lands on the spine (same as the automation
 * worker's own sends) so the Inbox and any future automation trigger see it
 * for real, plus a contact_notes row for the full readable text on the
 * contact's Notes tab. The demo preview has no activity_events table, so it
 * keeps its original session-local "sent" list instead.
 */

const IS_DEMO = import.meta.env.VITE_IS_DEMO === "true";

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export interface SentEmail {
  id: string;
  contactId: string | number;
  contactName: string;
  to: string;
  subject: string;
  /** HTML body (rendered in the Inbox reading pane). */
  body: string;
  date: string;
}

export const useSignature = () => {
  const { identity } = useGetIdentity();
  return useStore<string>(
    "slate.emailSignature",
    identity?.fullName ? `-\n${identity.fullName}` : "-",
  );
};

/** Minimal HTML → markdown-ish text for the timeline log. */
const htmlToLogText = (html: string): string =>
  html
    .replace(/<img[^>]*>/gi, "[image]")
    .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, "[$2]($1)")
    .replace(/<(strong|b)>(.*?)<\/\1>/gi, "**$2**")
    .replace(/<(em|i)>(.*?)<\/\1>/gi, "*$2*")
    .replace(/<li[^>]*>/gi, "\n- ")
    .replace(/<br[^>]*>/gi, "\n")
    .replace(/<\/(div|p|ul|ol)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const ToolbarButton = ({
  icon: Icon,
  title,
  onClick,
}: {
  icon: typeof Bold;
  title: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => {
      e.preventDefault(); // keep the editor selection
      onClick();
    }}
    className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
  >
    <Icon className="w-3.5 h-3.5" />
  </button>
);

export const EmailComposeSheet = ({
  contact,
  open,
  onOpenChange,
}: {
  contact: Contact;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const { data: me } = useGetOne<Sale>(
    "sales",
    { id: identity?.id as any },
    { enabled: identity?.id != null },
  );
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [signature, setSignature] = useSignature();
  const [sent, setSent] = useStore<SentEmail[]>("slate.sentEmails", []);

  const editorRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const defaultTo = contact.email_jsonb?.[0]?.email ?? "";
  const [to, setTo] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [editingSig, setEditingSig] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [sending, setSending] = useState(false);

  const effTo = to ?? defaultTo;

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const insertHtml = (html: string) => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
  };

  const insertMeetingLink = async () => {
    if (identity?.id == null) return;
    const first = identity.fullName?.split(" ")[0] ?? "me";
    let page: Awaited<ReturnType<typeof loadBookingPage>> = null;
    try {
      page = await loadBookingPage(identity.id);
    } catch {
      // Not set up in this workspace (or the demo) - same as no page.
    }
    if (!page?.enabled) {
      notify(
        "Your meeting scheduler isn't on yet - set it up under your avatar → Profile.",
        { type: "info" },
      );
      return;
    }
    insertHtml(
      `&nbsp;<a href="${bookingLinkFor(page.handle)}">Book time with ${escapeHtml(first)}</a>&nbsp;`,
    );
  };

  const onPickImage = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      insertHtml(
        `<img src="${reader.result}" style="max-width:100%;border-radius:4px" alt="${file.name}" />`,
      );
    };
    reader.readAsDataURL(file);
  };

  const send = async () => {
    const bodyHtml = editorRef.current?.innerHTML ?? "";
    const bodyText = htmlToLogText(bodyHtml);
    if (!subject.trim() && !bodyText) return;
    const fullBodyHtml = `${bodyHtml}<p style="white-space:pre-line">${signature}</p>`;
    const effSubject = subject.trim() || "(no subject)";

    setSending(true);

    let delivered: boolean;
    try {
      const result = await callNylasApi<{ id: string }>("/integrations/nylas/send", {
        method: "POST",
        body: {
          to: [{ email: effTo, name: `${contact.first_name} ${contact.last_name}`.trim() }],
          subject: effSubject,
          body: fullBodyHtml,
        },
      });
      delivered = result !== null; // null = no account connected, not an error
    } catch (e) {
      // A real send was attempted and failed - don't log a note claiming it
      // went out, and let them fix/retry instead of losing the draft.
      setSending(false);
      notify(e instanceof Error ? e.message : "Couldn't send that email. Please try again.", {
        type: "error",
      });
      return;
    }

    // Auto-register on the timeline as an email activity - same shape the
    // live mailbox sync writes.
    await dataProvider.create("contact_notes", {
      data: {
        contact_id: contact.id,
        text: `📧 **${effSubject}**\nTo: ${effTo}\n\n${bodyText}\n\n${signature}`,
        date: new Date().toISOString(),
        sales_id: identity?.id,
        status: contact.status,
        activity_type: "email",
      },
    });
    await update("contacts", {
      id: contact.id,
      data: { last_seen: new Date().toISOString() },
      previousData: contact,
    });
    if (IS_DEMO) {
      setSent([
        {
          id: String(Date.now()),
          contactId: contact.id,
          contactName: `${contact.first_name} ${contact.last_name}`,
          to: effTo,
          subject: effSubject,
          body: fullBodyHtml,
          date: new Date().toISOString(),
        },
        ...sent,
      ]);
    } else {
      const now = new Date().toISOString();
      const payload: EmailPayload = {
        direction: "outbound",
        subject: effSubject,
        fromAddress: me?.email ?? "",
        toAddresses: [effTo],
        bodyPreview: bodyText.slice(0, 300),
        capturedVia: "compose",
      };
      await dataProvider.create("activity_events", {
        data: {
          id: crypto.randomUUID(),
          type: "email.sent",
          occurred_at: now,
          recorded_at: now,
          actor:
            identity?.id != null
              ? { kind: "user", userId: String(identity.id) }
              : { kind: "system" },
          contact_id: contact.id,
          payload,
        },
      });
    }
    if (editorRef.current) editorRef.current.innerHTML = "";
    setSending(false);
    setSubject("");
    onOpenChange(false);
    notify(
      delivered
        ? `Sent to ${effTo} and logged to the timeline.`
        : "Logged to the timeline. Connect your Google or Microsoft account under Settings → My profile to actually deliver emails.",
      { type: delivered ? "success" : "info" },
    );
    refresh();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-2 border-b">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4" />
            Email {contact.first_name} {contact.last_name}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Sends through your connected Google or Microsoft account and logs
            to the timeline. Haven't connected one? It'll just log for now -
            set it up under Settings → My profile.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
          <div className="grid grid-cols-[2rem_1fr] items-center gap-2">
            <p className="text-xs text-muted-foreground">To</p>
            <Input
              value={effTo}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 text-sm"
            />
            <p className="text-xs text-muted-foreground">Subj.</p>
            <Input
              placeholder="Subject…"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-0.5 rounded-md border border-border px-1 py-0.5">
            <ToolbarButton icon={Bold} title="Bold" onClick={() => exec("bold")} />
            <ToolbarButton icon={Italic} title="Italic" onClick={() => exec("italic")} />
            <ToolbarButton icon={Underline} title="Underline" onClick={() => exec("underline")} />
            <span className="w-px h-4 bg-border mx-1" />
            <ToolbarButton icon={List} title="Bulleted list" onClick={() => exec("insertUnorderedList")} />
            <span className="w-px h-4 bg-border mx-1" />
            <ToolbarButton icon={AlignLeft} title="Align left" onClick={() => exec("justifyLeft")} />
            <ToolbarButton icon={AlignCenter} title="Align center" onClick={() => exec("justifyCenter")} />
            <ToolbarButton icon={AlignRight} title="Align right" onClick={() => exec("justifyRight")} />
            <span className="w-px h-4 bg-border mx-1" />
            <ToolbarButton
              icon={Link2}
              title="Insert link (select text first)"
              onClick={() => setShowLink((v) => !v)}
            />
            <ToolbarButton
              icon={ImageIcon}
              title="Insert image"
              onClick={() => fileRef.current?.click()}
            />
            <button
              type="button"
              title="Insert your meeting link so they can book time on your calendar"
              onMouseDown={(e) => {
                e.preventDefault();
                void insertMeetingLink();
              }}
              className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors ml-1"
            >
              <CalendarClock className="w-3 h-3" /> Meeting link
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onPickImage(e.target.files?.[0])}
            />
          </div>
          {showLink && (
            <div className="flex items-center gap-1.5">
              <Input
                className="h-7 text-xs flex-1"
                placeholder="https://… (select text in the message first)"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (linkUrl.trim()) exec("createLink", linkUrl.trim());
                  setShowLink(false);
                  setLinkUrl("");
                }}
              >
                Apply
              </Button>
            </div>
          )}

          {/* Rich editor */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            data-placeholder={`Hi ${contact.first_name},`}
            className="min-h-52 rounded-md border border-input px-3 py-2 text-sm leading-6 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&_a]:underline [&_a]:text-[#2e6ba9] [&_ul]:list-disc [&_ul]:pl-5 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground"
          />

          <div className="rounded-md border border-border p-2">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium flex-1">
                Signature
              </p>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                onClick={() => setEditingSig((v) => !v)}
              >
                <PenLine className="w-3 h-3" />
                {editingSig ? "Done" : "Edit"}
              </button>
            </div>
            {editingSig ? (
              <Textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className="min-h-20 text-sm"
              />
            ) : (
              <p className="text-sm whitespace-pre-line text-muted-foreground">
                {signature}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground mt-1">
              Saved per user. The mailbox sync can pull your real signature at
              deployment.
            </p>
          </div>
        </div>

        <div className="border-t p-3 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={sending} onClick={send}>
            <Send className="w-4 h-4" /> Send & log
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
